import { useGetList } from "ra-core";
import { useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { format, startOfMonth, subMonths } from "date-fns";
import {
  DollarSign,
  Trophy,
  TrendingUp,
  Calculator,
  BarChart3,
  Activity,
  Phone,
  Mail,
  MapPin,
  FileText,
} from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type {
  Job,
  Activity as ActivityType,
  Sale,
} from "../providers/fakerest/dataGenerator/types";
import {
  JOB_STAGES,
  findStageLabel,
  findStageColor,
} from "../resources/jobs/jobStages";

// Summary Card Component
const SummaryCard = ({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}) => (
  <Card className="group hover:border-primary/30 transition-all duration-300">
    <CardHeader className="flex flex-row items-center space-y-0 pb-2">
      <div className="p-2 rounded-lg bg-primary/10 mr-3 group-hover:bg-primary/20 transition-colors duration-300">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </CardContent>
  </Card>
);

// Jobs by Status Chart
const JobsByStatusChart = ({ jobs }: { jobs: Job[] }) => {
  const pipelineStages = JOB_STAGES.filter(
    (stage) => !["completed", "lost"].includes(stage.value),
  );

  const chartData = useMemo(() => {
    return pipelineStages.map((stage, index) => {
      const count = jobs.filter((job) => job.status === stage.value).length;
      return {
        status: stage.label,
        shortLabel: `${index + 1}`,
        count,
      };
    });
  }, [jobs]);

  const colors: Record<string, string> = {
    "New Lead": "#3b82f6",
    "Inspection Scheduled": "#eab308",
    "Proposal Sent": "#a855f7",
    Negotiation: "#f97316",
    Won: "#22c55e",
    "In Progress": "#06b6d4",
  };

  return (
    <Card className="col-span-1">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="h-5 w-5" />
          Jobs by Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-sm">
          {pipelineStages.map((stage, index) => (
            <div key={stage.value} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: colors[stage.label] }}
              />
              <span className="text-muted-foreground">{stage.label}</span>
            </div>
          ))}
        </div>
        <div className="h-[250px]">
          <ResponsiveBar
            data={chartData}
            keys={["count"]}
            indexBy="status"
            margin={{ top: 10, right: 20, bottom: 30, left: 40 }}
            padding={0.3}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: true }}
            colors={({ data }) => colors[data.status as string] || "#6b7280"}
            enableLabel={true}
            labelTextColor="#ffffff"
            axisBottom={null}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
            }}
            enableGridY={true}
            gridYValues={5}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: "var(--color-muted-foreground)",
                  },
                },
              },
              grid: {
                line: {
                  stroke: "var(--color-border)",
                  strokeOpacity: 0.5,
                },
              },
            }}
            tooltip={({ data }) => (
              <div className="bg-popover text-popover-foreground px-3 py-2 rounded shadow-md text-sm">
                <strong>{data.status}:</strong> {data.count} jobs
              </div>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Revenue by Month Chart
const RevenueByMonthChart = ({ jobs }: { jobs: Job[] }) => {
  const chartData = useMemo(() => {
    const now = new Date();
    const months: { month: Date; label: string }[] = [];

    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = startOfMonth(subMonths(now, i));
      months.push({
        month: monthDate,
        label: format(monthDate, "MMM"),
      });
    }

    // Only count "won" jobs as revenue
    const wonJobs = jobs.filter((job) => job.status === "won");

    return months.map(({ month, label }) => {
      const monthStart = month;
      const monthEnd = startOfMonth(subMonths(monthStart, -1));

      const monthRevenue = wonJobs
        .filter((job) => {
          const jobDate = new Date(job.updated_at);
          return jobDate >= monthStart && jobDate < monthEnd;
        })
        .reduce((sum, job) => sum + job.estimated_value, 0);

      return {
        month: label,
        revenue: monthRevenue,
      };
    });
  }, [jobs]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  return (
    <Card className="col-span-1">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5" />
          Revenue by Month
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveBar
            data={chartData}
            keys={["revenue"]}
            indexBy="month"
            margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
            padding={0.3}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: true }}
            colors={["#22c55e"]}
            enableLabel={false}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              format: (v) => formatCurrency(v as number),
            }}
            enableGridY={true}
            gridYValues={5}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: "var(--color-muted-foreground)",
                  },
                },
              },
              grid: {
                line: {
                  stroke: "var(--color-border)",
                  strokeOpacity: 0.5,
                },
              },
            }}
            tooltip={({ data }) => (
              <div className="bg-popover text-popover-foreground px-3 py-2 rounded shadow-md text-sm">
                <strong>{data.month}:</strong>{" "}
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                }).format(data.revenue as number)}
              </div>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Activity Feed Component
const ActivityFeed = ({
  activities,
  salesUsers,
}: {
  activities: ActivityType[];
  salesUsers: Sale[];
}) => {
  const typeIcons: Record<
    string,
    React.ComponentType<{ className?: string }>
  > = {
    call: Phone,
    email: Mail,
    note: FileText,
    site_visit: MapPin,
    status_change: Activity,
  };

  const typeColors: Record<string, string> = {
    call: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    email:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    note: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
    site_visit:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    status_change:
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  };

  const getUserById = (userId: number) =>
    salesUsers?.find((u) => u.id === userId);

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return format(date, "MMM d");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activities.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">
              No recent activities
            </p>
          ) : (
            activities.map((activity) => {
              const Icon = typeIcons[activity.type] || Activity;
              const activityUser = getUserById(activity.user_id);
              return (
                <Link
                  key={activity.id}
                  to={`/jobs/${activity.job_id}/show`}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-all duration-200 hover:translate-x-1"
                >
                  {/* User avatar */}
                  {activityUser ? (
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage
                        src={activityUser.avatar?.src}
                        alt={`${activityUser.first_name} ${activityUser.last_name}`}
                      />
                      <AvatarFallback>
                        {activityUser.first_name?.[0]}
                        {activityUser.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div
                      className={`p-2 rounded-full ${typeColors[activity.type] || "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"}`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-medium">
                        {activityUser
                          ? `${activityUser.first_name} ${activityUser.last_name}`
                          : "Unknown"}
                      </span>
                      <Badge
                        variant="secondary"
                        className={`text-xs ${typeColors[activity.type] || "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"}`}
                      >
                        {activity.type.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm truncate">{activity.description}</p>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
export const Dashboard = () => {
  const { data: jobs, isPending: jobsLoading } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: activities, isPending: activitiesLoading } =
    useGetList<ActivityType>("activities", {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "timestamp", order: "DESC" },
    });

  const { data: salesUsers } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  // Calculate summary metrics
  const metrics = useMemo(() => {
    if (!jobs) {
      return {
        pipelineValue: 0,
        jobsWonThisMonth: 0,
        winRate: 0,
        avgJobValue: 0,
      };
    }

    // Pipeline value: sum of all non-lost, non-completed jobs
    const pipelineJobs = jobs.filter(
      (job) => !["lost", "completed"].includes(job.status),
    );
    const pipelineValue = pipelineJobs.reduce(
      (sum, job) => sum + job.estimated_value,
      0,
    );

    // Jobs won this month
    const now = new Date();
    const monthStart = startOfMonth(now);
    const jobsWonThisMonth = jobs.filter((job) => {
      if (job.status !== "won") return false;
      const jobDate = new Date(job.updated_at);
      return jobDate >= monthStart;
    }).length;

    // Win rate: won / (won + lost)
    const wonJobs = jobs.filter((job) => job.status === "won").length;
    const lostJobs = jobs.filter((job) => job.status === "lost").length;
    const winRate =
      wonJobs + lostJobs > 0 ? (wonJobs / (wonJobs + lostJobs)) * 100 : 0;

    // Average job value (all jobs)
    const avgJobValue =
      jobs.length > 0
        ? jobs.reduce((sum, job) => sum + job.estimated_value, 0) / jobs.length
        : 0;

    return {
      pipelineValue,
      jobsWonThisMonth,
      winRate,
      avgJobValue,
    };
  }, [jobs]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  if (jobsLoading || activitiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Pipeline Value"
          value={formatCurrency(metrics.pipelineValue)}
          icon={DollarSign}
          description="Active opportunities"
        />
        <SummaryCard
          title="Jobs Won This Month"
          value={metrics.jobsWonThisMonth.toString()}
          icon={Trophy}
          description={format(new Date(), "MMMM yyyy")}
        />
        <SummaryCard
          title="Win Rate"
          value={`${metrics.winRate.toFixed(1)}%`}
          icon={TrendingUp}
          description="Won vs Lost"
        />
        <SummaryCard
          title="Average Job Value"
          value={formatCurrency(metrics.avgJobValue)}
          icon={Calculator}
          description="Across all jobs"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <JobsByStatusChart jobs={jobs || []} />
        <RevenueByMonthChart jobs={jobs || []} />
      </div>

      {/* Activity Feed */}
      <ActivityFeed
        activities={activities || []}
        salesUsers={salesUsers || []}
      />
    </div>
  );
};
